/**
 * The main loop.
 *
 * Per frame, in order:
 *   1. `profiler.beginFrame`
 *   2. `scheduler.advance(now)` → N physics ticks, each `fixedUpdate(1/120)`   [Channel.Physics]
 *   3. `streamUpdate(frameDt)` — amortised terrain/tile work                    [Channel.Terrain]
 *   4. `frameUpdate(alpha, frameDt)` — interpolate render state from sim state  [Channel.RenderCPU]
 *   5. `render()`                                                              [Channel.RenderCPU]
 *   6. `audioUpdate(frameDt)` — push the frame's sound parameters              [Channel.Audio]
 *   7. `profiler.endFrame`, then `afterFrame()` for overlays and the governor
 *
 * Allocation discipline: the frame callback is a bound arrow created once, the callbacks are
 * fields (no closure rebuilding), and nothing in this file constructs an object per frame.
 * `time.now()` is `performance.now()` — a number, not a Date.
 */

import { FixedStepScheduler, type FixedStepSchedulerOptions } from './scheduler.js';
import {
  ALLOCATION_SLOT_RENDER_SUBMIT,
  AllocationProbe,
  Channel,
  Profiler,
  type ProfilerOptions,
} from './profiler.js';

export interface GameLoopCallbacks {
  /** Fixed 120 Hz simulation step. `dtSeconds` is always `scheduler.stepSeconds`. */
  fixedUpdate?(dtSeconds: number, tickIndex: number): void;
  /** Amortised streaming/IO polling. Called once per frame with the real frame delta. */
  streamUpdate?(frameDtSeconds: number): void;
  /** Build render state by interpolating the last two sim states with `alpha`. */
  frameUpdate?(alpha: number, frameDtSeconds: number): void;
  /** Submit the frame. */
  render?(): void;
  /**
   * Push the frame's audio parameters.
   *
   * After `render` rather than before it, for one reason and one only: each section boundary
   * costs a `now()` call, and slotting audio between the interpolation and the submission would
   * split the render-CPU section in two and cost an extra one every frame. The price is that the
   * sound is derived from the state one frame after the picture — eight milliseconds at 120 fps,
   * which is below the threshold at which anyone can hear a picture and a sound as separate
   * events, and an order of magnitude below the audio buffer it is written into anyway.
   */
  audioUpdate?(frameDtSeconds: number): void;
  /** Overlays, governor, anything that may allocate a little. Runs outside the timed section. */
  afterFrame?(frameDtSeconds: number): void;
}

export interface GameLoopOptions extends GameLoopCallbacks {
  scheduler?: FixedStepSchedulerOptions;
  profiler?: ProfilerOptions;
  /** Injected for tests; defaults to `requestAnimationFrame`. */
  requestFrame?: (callback: (nowMs: number) => void) => number;
  /** Injected for tests; defaults to `cancelAnimationFrame`. */
  cancelFrame?: (handle: number) => void;
  /** Injected for tests; defaults to `performance.now`. */
  now?: () => number;
  /**
   * A per-channel allocation probe. Omitted in every normal session.
   *
   * When present the loop reads the heap at each channel boundary it already timestamps, which
   * turns "the frame allocates 54 KB" into "and here is which sixth of it does". It costs a
   * property read per boundary and is therefore opt-in — see `AllocationProbe`.
   */
  allocationProbe?: AllocationProbe;
}

export class GameLoop {
  readonly scheduler: FixedStepScheduler;
  readonly profiler: Profiler;

  private readonly callbacks: GameLoopCallbacks;
  private readonly requestFrame: (callback: (nowMs: number) => void) => number;
  private readonly cancelFrame: (handle: number) => void;
  private readonly now: () => number;
  private readonly allocationProbe: AllocationProbe | null;

  private handle = 0;
  private runningValue = false;
  private lastFrameStartMs = 0;
  private frameDtSeconds = 0;

  constructor(options: GameLoopOptions = {}) {
    this.scheduler = new FixedStepScheduler(options.scheduler);
    this.profiler = new Profiler(options.profiler);
    this.callbacks = options;
    this.requestFrame =
      options.requestFrame ?? ((cb) => globalThis.requestAnimationFrame(cb));
    this.cancelFrame = options.cancelFrame ?? ((h) => globalThis.cancelAnimationFrame(h));
    this.now = options.now ?? (() => globalThis.performance.now());
    this.allocationProbe = options.allocationProbe ?? null;
  }

  get running(): boolean {
    return this.runningValue;
  }

  /** Real time between the last two frame starts [s]. */
  get frameDelta(): number {
    return this.frameDtSeconds;
  }

  start(): void {
    if (this.runningValue) return;
    this.runningValue = true;
    this.lastFrameStartMs = this.now();
    this.scheduler.start(this.lastFrameStartMs);
    this.handle = this.requestFrame(this.onFrame);
  }

  stop(): void {
    if (!this.runningValue) return;
    this.runningValue = false;
    this.cancelFrame(this.handle);
    this.handle = 0;
  }

  /** Pause the *simulation*. Rendering continues, so the UI stays live and the view orbits. */
  pause(): void {
    this.scheduler.pause();
  }

  resume(): void {
    this.scheduler.resume();
  }

  togglePause(): void {
    this.scheduler.setPaused(!this.scheduler.paused);
  }

  /** Advance exactly `count` physics ticks while paused. */
  stepOnce(count = 1): void {
    this.scheduler.requestSingleStep(count);
  }

  /** Run one frame by hand — used by tests and by the headless perf harness. */
  runFrame(nowMs: number): void {
    this.onFrame(nowMs);
  }

  private readonly onFrame = (nowMs: number): void => {
    const frameDtMs = nowMs - this.lastFrameStartMs;
    this.lastFrameStartMs = nowMs;
    this.frameDtSeconds = frameDtMs / 1000;

    const profiler = this.profiler;
    const cb = this.callbacks;
    profiler.beginFrame(nowMs);

    /*
     * One timestamp per section boundary, not one per begin() *and* one per end(): the end of
     * physics is the start of streaming. Four `now()` calls a frame instead of seven, no
     * unattributed gaps between sections — and, because `now` is an instance field rather
     * than a directly-callable builtin, each avoided call is also a double the JIT no longer
     * has to box on its way out. The allocation guard notices the difference.
     */
    const probe = this.allocationProbe;
    probe?.beginFrame();

    const frameStart = this.now();
    let sectionStart = frameStart;

    // --- physics: exactly 120 Hz, decoupled from the display -------------------------
    const ticks = this.scheduler.advance(nowMs);
    if (ticks > 0 && cb.fixedUpdate !== undefined) {
      const dt = this.scheduler.stepSeconds;
      const firstTick = this.scheduler.totalTicks - ticks;
      for (let i = 0; i < ticks; i++) {
        cb.fixedUpdate(dt, firstTick + i);
      }
    }
    const afterPhysics = this.now();
    profiler.addSpan(Channel.Physics, afterPhysics - sectionStart);
    sectionStart = afterPhysics;
    probe?.mark(Channel.Physics);

    // --- streaming: amortised, budget 2 ms ------------------------------------------
    if (cb.streamUpdate !== undefined) cb.streamUpdate(this.frameDtSeconds);
    const afterStream = this.now();
    profiler.addSpan(Channel.Terrain, afterStream - sectionStart);
    sectionStart = afterStream;
    probe?.mark(Channel.Terrain);

    // --- render CPU: interpolate, then submit ---------------------------------------
    if (cb.frameUpdate !== undefined) cb.frameUpdate(this.scheduler.alpha, this.frameDtSeconds);
    /*
     * The probe splits our interpolation from three's submit here; the timer deliberately does
     * not. See `ALLOCATION_SLOT_RENDER_SUBMIT` — this boundary is the line between garbage we can
     * fix and garbage three makes on our behalf, and it costs nothing unless the probe exists.
     */
    probe?.mark(Channel.RenderCPU);
    if (cb.render !== undefined) cb.render();
    const afterRender = this.now();
    profiler.addSpan(Channel.RenderCPU, afterRender - sectionStart);
    sectionStart = afterRender;
    probe?.mark(ALLOCATION_SLOT_RENDER_SUBMIT);

    // --- audio: parameter writes only, budget 0.5 ms ---------------------------------
    // The synthesis happens on the audio thread; what is timed here is this thread's share.
    if (cb.audioUpdate !== undefined) cb.audioUpdate(this.frameDtSeconds);
    const afterAudio = this.now();
    profiler.addSpan(Channel.Audio, afterAudio - sectionStart);
    probe?.mark(Channel.Audio);

    profiler.endFrame(afterAudio);

    if (cb.afterFrame !== undefined) cb.afterFrame(this.frameDtSeconds);
    // `afterFrame` is charged to Other, the same channel the timing breakdown charges it to.
    probe?.mark(Channel.Other);
    probe?.endFrame();

    if (this.runningValue) this.handle = this.requestFrame(this.onFrame);
  };
}
